import { css } from '@emotion/react';

export const globalVariables = {
  // colors
  white: '#fff',
  black: '#000',
  primary: '#51abf3',
  secondary: '#33b893',
  warning: '#ffbf00',
  error: '#fa5b4a',

  gray_50: '#f8f9fa',
  gray_60: '#f5f6f7',
  gray_80: '#f1f3f5',
  gray_100: '#e9ecef',
  gray_200: '#dee2e6',
  gray_300: '#ced4da',
  gray_400: '#adb5bd',
  gray_500: '#848c94',
  gray_600: '#666d75',
  gray_700: '#495056',
  gray_800: '#343a40',
  gray_900: '#212529',

  blue_50: '#f5fbff',
  blue_80: '#e7f4fd',
  blue_100: '#cbe7fd',
  blue_200: '#a7d4f9',
  blue_300: '#79bef5',
  blue_400: '#51abf3',
  blue_500: '#2b96ed',
  blue_600: '#1583db',
  blue_700: '#0a70c2',
  blue_800: '#025ba5',
  blue_900: '#01457d',

  green_50: '#e6f8f3',
  green_80: '#d7f6ee',
  green_100: '#94e7d1',
  green_200: '#71d2b8',
  green_300: '#58caab',
  green_400: '#33b893',
  green_500: '#27ab86',
  green_600: '#1c9674',
  green_700: '#137b5e',
  green_800: '#10634c',
  green_900: '#0a4534',

  yellow_50: '#fff8e1',
  yellow_80: '#fff2c6',
  yellow_100: '#ffe182',
  yellow_200: '#ffd74e',
  yellow_300: '#ffc929',
  yellow_400: '#ffbf00',
  yellow_500: '#ffad01',
  yellow_600: '#ff9a00',
  yellow_700: '#f78000',
  yellow_800: '#db5600',
  yellow_900: '#b94100',

  red_50: '#fbf1ef',
  red_80: '#ffd4cc',
  red_100: '#ffaea1',
  red_200: '#ff8d7c',
  red_300: '#fe6a54',
  red_400: '#fa5b4a',
  red_500: '#ec4937',
  red_600: '#dd3c2a',
  red_700: '#c83323',
  red_800: '#aa2a1b',
  red_900: '#8d2115',

  // elevation
  elevation_z1: '0 2px 4px 0 rgba(33, 37, 41, 0.1)',
  elevation_z2: '0 4px 8px 0 rgba(33, 37, 41, 0.1)',
  elevation_z3: '0 8px 16px 0 rgba(33, 37, 41, 0.15)',
  elevation_z4: '0 12px 24px 0 rgba(33, 37, 41, 0.15)',

  elevation_z0_border: '0 0 0 1px var(--gray-100)',
  elevation_z1_border: `0 2px 4px 0 rgba(0, 0, 0, 0.1),
    0 0 0 1px var(--gray-100)`,
  elevation_z2_border: `0 4px 8px 0 rgba(33, 37, 41, 0.1),
    0 0 0 1px var(--gray-100)`,
  elevation_z3_border: `0 8px 16px 0 rgba(33, 37, 41, 0.15),
    0 0 0 1px var(--gray-100)`,
  elevation_z4_border: `0 12px 24px 0 rgba(33, 37, 41, 0.15),
    0 0 0 1px var(--gray-100)`,

  // easing
  motion_easing_basic: 'cubic-bezier(0.4, 0, 0.2, 1)',
  motion_easing_linear_out: 'cubic-bezier(0, 0, 0.2, 1)',
  motion_easing_linear_in: 'cubic-bezier(0.4, 0, 1, 1)',

  // z-index
  z_toast: 60000,
  z_screen_loading: 50000,
  z_alert: 40000,
  z_alert_above_screen_loading: 51000,
  z_popup: 30000,
  z_dialog: 20000,
  z_bottomsheet: 10000,
  z_bottomsheet_above_dialog: 21000,
};

export const DesignSystemGlobalVariables = css`
  :root {
    // colors
    --white: ${globalVariables.white};
    --black: ${globalVariables.black};
    --primary: ${globalVariables.primary};
    --secondary: ${globalVariables.secondary};
    --warning: ${globalVariables.warning};
    --error: ${globalVariables.error};

    --gray-50: ${globalVariables.gray_50};
    --gray-60: ${globalVariables.gray_60};
    --gray-80: ${globalVariables.gray_80};
    --gray-100: ${globalVariables.gray_100};
    --gray-200: ${globalVariables.gray_200};
    --gray-300: ${globalVariables.gray_300};
    --gray-400: ${globalVariables.gray_400};
    --gray-500: ${globalVariables.gray_500};
    --gray-600: ${globalVariables.gray_600};
    --gray-700: ${globalVariables.gray_700};
    --gray-800: ${globalVariables.gray_800};
    --gray-900: ${globalVariables.gray_900};

    --blue-50: ${globalVariables.blue_50};
    --blue-80: ${globalVariables.blue_80};
    --blue-100: ${globalVariables.blue_100};
    --blue-200: ${globalVariables.blue_200};
    --blue-300: ${globalVariables.blue_300};
    --blue-400: ${globalVariables.blue_400};
    --blue-500: ${globalVariables.blue_500};
    --blue-600: ${globalVariables.blue_600};
    --blue-700: ${globalVariables.blue_700};
    --blue-800: ${globalVariables.blue_800};
    --blue-900: ${globalVariables.blue_900};

    --green-50: ${globalVariables.green_50};
    --green-80: ${globalVariables.green_80};
    --green-100: ${globalVariables.green_100};
    --green-200: ${globalVariables.green_200};
    --green-300: ${globalVariables.green_300};
    --green-400: ${globalVariables.green_400};
    --green-500: ${globalVariables.green_500};
    --green-600: ${globalVariables.green_600};
    --green-700: ${globalVariables.green_700};
    --green-800: ${globalVariables.green_800};
    --green-900: ${globalVariables.green_900};

    --yellow-50: ${globalVariables.yellow_50};
    --yellow-80: ${globalVariables.yellow_80};
    --yellow-100: ${globalVariables.yellow_100};
    --yellow-200: ${globalVariables.yellow_200};
    --yellow-300: ${globalVariables.yellow_300};
    --yellow-400: ${globalVariables.yellow_400};
    --yellow-500: ${globalVariables.yellow_500};
    --yellow-600: ${globalVariables.yellow_600};
    --yellow-700: ${globalVariables.yellow_700};
    --yellow-800: ${globalVariables.yellow_800};
    --yellow-900: ${globalVariables.yellow_900};

    --red-50: ${globalVariables.red_50};
    --red-80: ${globalVariables.red_80};
    --red-100: ${globalVariables.red_100};
    --red-200: ${globalVariables.red_200};
    --red-300: ${globalVariables.red_300};
    --red-400: ${globalVariables.red_400};
    --red-500: ${globalVariables.red_500};
    --red-600: ${globalVariables.red_600};
    --red-700: ${globalVariables.red_700};
    --red-800: ${globalVariables.red_800};
    --red-900: ${globalVariables.red_900};

    // elevation
    --elevation-z1: ${globalVariables.elevation_z1};
    --elevation-z2: ${globalVariables.elevation_z2};
    --elevation-z3: ${globalVariables.elevation_z3};
    --elevation-z4: ${globalVariables.elevation_z4};

    --elevation-z0-border: ${globalVariables.elevation_z0_border};
    --elevation-z1-border: ${globalVariables.elevation_z1_border};
    --elevation-z2-border: ${globalVariables.elevation_z2_border};
    --elevation-z3-border: ${globalVariables.elevation_z3_border};
    --elevation-z4-border: ${globalVariables.elevation_z4_border};

    // easing
    --motion-easing-basic: ${globalVariables.motion_easing_basic};
    --motion-easing-linear-out: ${globalVariables.motion_easing_linear_out};
    --motion-easing-linear-in: ${globalVariables.motion_easing_linear_in};

    // z-index
    --z-toast: ${globalVariables.z_toast};
    --z-screen-loading: ${globalVariables.z_screen_loading};
    --z-alert: ${globalVariables.z_alert};
    --z-alert-above-screen-loading: ${globalVariables.z_alert_above_screen_loading};
    --z-popup: ${globalVariables.z_popup};
    --z-dialog: ${globalVariables.z_dialog};
    --z-bottomsheet: ${globalVariables.z_bottomsheet};
    --z-bottomsheet-above-dialog: ${globalVariables.z_bottomsheet_above_dialog};
  }
`;

export enum PLATFORM {
  DESKTOP = 'desktop',
  PHONE = 'phone',
  IOS = 'ios',
  ANDROID = 'android',
}

export const TAP_HIGHLIGHT_TRANSPARENT = 'mrt-tap-highlight-transparent';

export const S3_RESOURCE_ICONS = 'https://dffoxz5he03rp.cloudfront.net/icons';

export const S3_RESOURCE_LOGOS = 'https://dffoxz5he03rp.cloudfront.net/logos';

export enum BREAK_POINT {
  MOBILE = 768,
}
